import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthModal } from './shared/useAuthModal';
import { signupSchema } from './shared/validationSchemas';
import ModalWrapper from './shared/ModalWrapper';
import SocialLoginButton from './shared/SocialLoginButton';
import FormInput from './shared/FormInput';
import ErrorDisplay from './shared/ErrorDisplay';
import CloseButton from './shared/CloseButton';
import './AuthModal.css';

const SignupModal = ({ isOpen, onClose, onSwitchToLogin }) => {
  const { registerWithEmail, loginWithGoogleOAuth } = useAuth();
  const { isLoading, setIsLoading, error, clearError, handleSocialAuth } = useAuthModal();
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [localError, setLocalError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(signupSchema),
    defaultValues: {
      marketingConsent: true
    }
  });

  const handleClose = () => {
    setIsLoading(false);
    clearError();
    setLocalError('');
    setRegistrationSuccess(false);
    reset();
    onClose();
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    setLocalError('');
    clearError();
    try {
      await registerWithEmail({
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
      });
      setRegistrationSuccess(true);
    } catch (err) {
      setLocalError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    handleSocialAuth(loginWithGoogleOAuth, handleClose);
  };

  const signupShell = (body) => (
    <ModalWrapper isOpen={isOpen} onClose={handleClose} className="auth-modal-signup">
      <div className="auth-modal-content auth-modal-content-signup">
        <div className="auth-modal-panel">
          {body}
        </div>
      </div>
    </ModalWrapper>
  );

  if (registrationSuccess) {
    return signupShell(
      <>
        <div className="auth-modal-header auth-modal-header-signup">
          <div className="auth-modal-header-content">
            <h2 className="auth-modal-title-signup">Check your email</h2>
          </div>
          <CloseButton className="auth-modal-close-signup" onClick={handleClose} />
        </div>

        <div className="auth-modal-body auth-modal-body-signup">
          <div className="auth-success-message">
            <div className="auth-success-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="24" fill="#E6F4ED" />
                <path d="M16 24L22 30L32 18" stroke="#046242" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="auth-success-text">
              We've sent a verification link to your email address.
              Please check your inbox and click the link to activate your account.
            </p>
            <p className="auth-success-subtext">
              The link expires in 24 hours. Check your spam folder if you don't see it.
            </p>
          </div>

          <button
            type="button"
            className="auth-submit-btn auth-submit-btn-signup"
            onClick={onSwitchToLogin}
          >
            Go to Login
          </button>
        </div>
      </>
    );
  }

  return signupShell(
    <>
      <div className="auth-modal-header auth-modal-header-signup">
        <div className="auth-modal-header-content">
          <h2 className="auth-modal-title-signup">Join Occasia</h2>
          <p className="auth-modal-subtitle-signup">
            Book unique spaces directly from local hosts
          </p>
        </div>
        <CloseButton
          className="auth-modal-close-signup"
          onClick={handleClose}
        />
      </div>

      <div className="auth-modal-body auth-modal-body-signup">
        <form onSubmit={handleSubmit(onSubmit)} className="auth-form auth-form-signup">
          <div className="auth-form-row">
            <FormInput
              type="text"
              placeholder="First name"
              register={register}
              error={errors.first_name}
              name="first_name"
            />
            <FormInput
              type="text"
              placeholder="Last name"
              register={register}
              error={errors.last_name}
              name="last_name"
            />
          </div>

          <FormInput
            type="email"
            placeholder="Email"
            register={register}
            error={errors.email}
            name="email"
          />

          <FormInput
            type="password"
            placeholder="Password"
            register={register}
            error={errors.password}
            name="password"
          />

          <FormInput
            type="password"
            placeholder="Confirm password"
            register={register}
            error={errors.confirmPassword}
            name="confirmPassword"
          />

          <div className="auth-checkbox-group auth-checkbox-group-signup">
            <label className="auth-checkbox-label auth-checkbox-label-signup">
              <input
                type="checkbox"
                className="auth-checkbox"
                {...register('marketingConsent')}
              />
              <span className="auth-checkbox-text auth-checkbox-text-signup">
                I'd like to receive deals, inspiration, and marketing emails.
                <br className="auth-checkbox-break-signup" />
                You can opt out at any time from your account settings.
              </span>
            </label>
          </div>

          <ErrorDisplay error={localError || error} />

          <button
            type="submit"
            className="auth-submit-btn auth-submit-btn-signup"
            disabled={isLoading}
          >
            {isLoading ? 'Creating account...' : 'Sign Up'}
          </button>
        </form>

        <div className="auth-divider auth-divider-signup">
          <div className="auth-divider-line"></div>
          <span className="auth-divider-text auth-divider-text-signup">or</span>
          <div className="auth-divider-line"></div>
        </div>

        <div className="auth-social-buttons auth-social-buttons-signup">
          <SocialLoginButton
            provider="google"
            onClick={handleGoogleSignup}
            disabled={isLoading}
            loadingText="Signing up..."
          >
            Sign up with Google
          </SocialLoginButton>
        </div>

        <div className="auth-terms auth-terms-signup">
          <p className="auth-terms-text-signup">
            <span>By clicking Sign Up, you agree to </span>
            <span>Occasia's</span>
            <br />
            <a href="/terms" className="auth-terms-link auth-terms-link-signup">Terms of use</a>
            <span> </span>
            <span>and </span>
            <a href="/privacy" className="auth-terms-link auth-terms-link-signup">Privacy Policy</a>
          </p>
        </div>

        <div className="auth-modal-footer auth-modal-footer-signup">
          <p className="auth-switch-text-signup">
            Already have an account?{' '}
            <button
              type="button"
              className="auth-switch-link-signup"
              onClick={onSwitchToLogin}
            >
              Login
            </button>
          </p>
        </div>
      </div>
    </>
  );
};

export default SignupModal;
